import React, { useEffect, useState } from 'react'
import { Button, Checkbox, FormControlLabel, FormGroup, Grid, Typography } from '@mui/material'
import { useLocation, useNavigate } from 'react-router'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import Sidebar from './components/Sidebar';
import i18next from 'i18next';
import FormQuestions from './components/FormQuestions';
import swal from 'sweetalert';
import { postApi } from './api/services';
import { serviceDown } from './utils/UtilityFunctions';
import MainLoader from './components/MainLoader';
import FormQuestionsPreview from './components/FormQuestionsPreview';
import { useSelector } from 'react-redux';

const HealthDeclaration = () => {

  const navigate = useNavigate()
  const location = useLocation()
  const [gender, setGender] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [formQuestions, setFormQuestions] = useState([])
  const [formValues, setFormValues] = useState({})
  const [previewFormQuestions, setPreviewFormQuestions] = useState([])
  const [declarations, setDeclarations] = useState({
    dec1: '',
    dec2: '',
    dec3: '',
    dec4: ''
  })
  const [submitClicked, setSubmitClicked] = useState(false);
  const [formValuesMale, setFormValuesMale] = useState({
    user_id: '',
    date_of_birth: '',
    hair_loss_treatment: {},
    medications: {},
    allergies: {},
    hair_loss_duration: '',
    psychological_state: '',
    medical_history: {},
    scalp_conditions: {}
  });

  const [formValuesFemale, setFormValuesFemale] = useState({
    user_id: '',
    date_of_birth: '',
    menopause_state: '',
    start_of_hair_loss_during_menopause: '',
    hormone_therapy: '',
    hormone_sensitive_breast_cancer: '',
    history_of_endometriosis_or_uterine_fibroids: '',
    depression_or_dosage_of_antidepressants: '',
    allergies: {},
    dutasteride_allergy: '',
    medications: {},
    other_medications: {},
    hair_loss_treatment: {},
    scalp_conditions: '',
    recent_hair_products: ''
  });

  const locale = useSelector((state) => state.language.langData);
  
  const formQuestionsMale = [
    {
      title: "Duration of Hair Loss",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "hair_loss_duration",
      optionMap:  {
        option_1: 'Less than three months',
        option_2: 'More than three months' 
      },
      note: `It is useful to know how long the subject has been experiencing hair loss. Shedding that lasts less than three months may suggest a temporary condition such as telogen effluvium, often related to stress, illness, or nutritional deficiency, or even a complication of a medical treatment (such as anti-mitotic drugs). When hair loss persists for more than three months, androgenetic alopecia is the most likely cause.`,
      questionId: 1
    },
    {
      title: "Current and past Hair Loss Treatments",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "hair_loss_treatment",
      optionMap:  {
        option_1: 'Yes to former treatment',
        option_2: 'Yes to current treatment',
        option_3: 'No'
      },
      note: `This is an important question as many men with hair loss have tried different sets of products, sometimes prescription drugs.`,
      dependentQuestions: [
        {
          title: "Please specify",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "hair_loss_treatment_dependent",
          selectedOption: ['Yes to former treatment', 'Yes to current treatment'],
          subQuestionId: 2
        }
      ],
      questionId: 2
    },
    {
      title: "Use of 5-alpha-reductase Inhibitors",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "medications",
      optionMap:  {
        option_1: 'Yes',
        option_2: 'No' 
      },
      note: `Medications such as Avodart, Combodart, Duodart, Jalyn, Dutasteride, Proscar, and Propecia (finasteride) are oral drugs, which may have been prescribed for hair loss or prostate conditions. If the subject is currently taking one of these, additional topical dutasteride should not be combined. You may consider switching from oral to topical therapy, especially if the subject mentions side effects of the oral therapy.`,
      dependentQuestions: [
        {
          title: "Please specify",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "medications_dependent",
          selectedOption: ['Yes'],
          subQuestionId: 3
        }
      ],
      questionId: 3
    },
    {
      title: "Psychological State",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "psychological_state",
      optionMap:  {
        option_1: 'Yes',
        option_2: 'No' 
      },
      note: `It is important to know if the subject is currently experiencing symptoms of depression or taking antidepressants. Regulatory authorities (FDA, EMA) have highlighted possible mood-related side effects with finasteride, as concerning as suicidal ideation. This risk has been found with oral formulations. Although they have indicated that dutasteride has not shown the same risk profile, and the formulation to be prescribed is topical, caution is necessary.`,
      questionId: 4
    },
    {
      title: "Current Medication",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "allergies",
      optionMap:  {
        option_1: 'Yes',
        option_2: 'No' 
      },
      note: `Some drugs may interact with dutasteride, but it has only been proven by measuring plasmatic concentrations with oral formulations. Particular attention is required for strong inhibitors of P450 (ketoconazole, ritonavir, verapamil, diltiazem) which may increase the plasma concentration of dutasteride and strong inductors (rifampicin, carbamazepine, dexamethasone), which may decrease it. The metabolism of topical formulations may not be affected, but it is useful to know about such interactions.`,
      dependentQuestions: [
        {
          title: "Please specify",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "allergies_dependent",
          selectedOption: ['Yes'],
          subQuestionId: 5
        }
      ],
      questionId: 5
    },
    {
      title: "Scalp Conditions",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "scalp_conditions",
      optionMap:  {
        option_1: 'Eczema',
        option_2: 'Red patches',
        option_3: 'Seborrheic dermatitis',
        option_4: 'Psoriasis',
        option_5: 'Inflammation or flaking'
      },
      note: `Inflammatory or chronic scalp conditions can interfere with the safe and effective use of topical treatments. Eczema, dermatitis, psoriasis, or red patches may reduce absorption, increase the risk of irritation, or worsen with application. On the whole, chronic scalp conditions should be a contraindication to a topical formulation of dutasteride.`,
      questionId: 6
    },
    {
      title: "Medical History",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "medical_history",
      optionMap:  {
        option_1: 'Prostate cancer',
        option_2: 'Breast cancer',
        option_3: 'Severe liver failure',
        option_4: 'Other chronic conditions'
      },
      note: `Some health conditions require special caution before prescribing dutasteride:
      · Prostate cancer: Dutasteride can alter PSA (prostate-specific antigen) levels and mask the progression of prostate disease.
      · Male breast cancer: Though rare, DHT-blocking agents may influence hormone-sensitive cancers.
      · Severe liver disease: Dutasteride is metabolized in the liver; reduced function increases the risk of drug accumulation.
      · Other chronic illnesses: Because of eventual treatments that might interact.`,
      dependentQuestions: [
        {
          title: "Please specify",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "medical_history_dependent",
          selectedOption: ['Other chronic conditions'],
          subQuestionId: 7
        }
      ],
      questionId: 7
    },
  ];

  const formQuestionsMaleFr = [
    {
      title: "Durée de la perte de cheveux",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "hair_loss_duration",
      optionMap:  {
        option_1: 'Moins de trois mois',
        option_2: 'Plus de trois mois' 
      },
      note: `Il est important de connaître depuis combien de temps le patient présente une chute capillaire. Une perte de cheveux qui dure moins de trois mois peut évoquer une affection transitoire telle que l’effluvium télogène, souvent liée au stress, à une pathologie aiguë, à une carence nutritionnelle, ou encore à une complication d’un traitement médical (notamment les agents anti-mitotiques). Lorsque la chute persiste au-delà de trois mois, l’alopécie androgénétique devient l’étiologie la plus probable.`,
      questionId: 1
    },
    {
      title: "Traitements actuels et antérieurs contre la perte de cheveux",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "hair_loss_treatment",
      optionMap:  {
        option_1: 'Oui, traitement antérieur',
        option_2: 'Oui, traitement en cours',
        option_3: 'Non'
      },
      note: `Il s’agit d’une question importante, car de nombreux hommes souffrant d’alopécie ont déjà expérimenté divers produits, parfois des médicaments soumis à prescription.`,
      dependentQuestions: [
        {
          title: "veuillez préciser",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "hair_loss_treatment_dependent",
          selectedOption: ['Oui, traitement antérieur', 'Oui, traitement en cours'],
          subQuestionId: 2
        }
      ],
      questionId: 2
    },
    {
      title: "Utilisation d’inhibiteurs de la 5-alpha-réductase",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "medications",
      optionMap:  {
        option_1: 'Oui',
        option_2: 'Non' 
      },
      note: `Médicaments tels que Avodart, Combodart, Duodart, Jalyn, Dutastéride, Proscar, Propecia, ou finasteride. Ce sont des traitements oraux qui peuvent avoir été prescrits pour la chute de cheveux ou pour des pathologies prostatiques. Si le patient prend actuellement l’un de ces médicaments, il est déconseillé d’associer un traitement topique à base de dutasteride mais est possible d’envisager une substitution du traitement oral par une forme topique, notamment si le patient rapporte des effets indésirables liés à la voie orale.`,
      dependentQuestions: [
        {
          title: "Please specify",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "medications_dependent",
          selectedOption: ['Oui'],
          subQuestionId: 3
        }
      ],
      questionId: 3
    },
    {
      title: "Santé mentale",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "psychological_state",
      optionMap:  {
        option_1: 'Oui',
        option_2: 'Non' 
      },
      note: `Il est essentiel de savoir si le patient présente actuellement des symptômes de dépression ou s’il suit un traitement antidépresseur. Les autorités réglementaires (FDA, EMA) ont signalé des effets indésirables psychiatriques potentiels liés au finastéride, incluant des troubles de l’humeur et des idées suicidaires. Ce risque a été observé avec les formulations orales. Bien que le dutastéride ne présente pas le même profil de risque selon les données disponibles, et que la formulation envisagée soit topique, une vigilance particulière reste recommandée. `,
      questionId: 4
    },
    {
      title: "Traitement médicamenteux en cours",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "allergies",
      optionMap:  {
        option_1: 'Oui',
        option_2: 'Non' 
      },
      note: `Certains médicaments peuvent interagir avec le dutastéride, bien que cela n’ait été démontré qu’en mesurant les concentrations plasmatiques avec les formulations orales. Une attention particulière doit être portée aux inhibiteurs puissants du cytochrome P450 (kétoconazole, ritonavir, vérapamil, diltiazem), qui peuvent augmenter la concentration plasmatique de dutastéride, ainsi qu’aux inducteurs puissants (rifampicine, carbamazépine, dexaméthasone), qui peuvent avoir l’effet inverse. Le métabolisme des formulations topiques pourrait ne pas être affecté, mais il reste utile de connaître ces interactions. `,
      dependentQuestions: [
        {
          title: "veuillez préciser",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "allergies_dependent",
          selectedOption: ['Oui'],
          subQuestionId: 5
        }
      ],
      questionId: 5
    },
    {
      title: "Affections du cuir chevelu",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "scalp_conditions",
      optionMap:  {
        option_1: 'Eczéma',
        option_2: 'Plaques rouges',
        option_3: 'Dermatite séborrhéique',
        option_4: 'Psoriasis',
        option_5: 'Inflammation ou desquamation'
      },
      note: `Les affections inflammatoires ou chroniques du cuir chevelu peuvent compromettre l’utilisation sûre et efficace des traitements topiques. L’eczéma, la dermatite, le psoriasis ou la présence de plaques rouges peuvent réduire l’absorption du produit, augmenter le risque d’irritation ou s’aggraver avec l’application. De manière générale, les pathologies chroniques du cuir chevelu doivent être considérées comme une contre-indication à l’utilisation topique du dutastéride.`,
      questionId: 6
    },
    {
      title: "Antécédents médicaux",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "medical_history",
      optionMap:  {
        option_1: 'Cancer de la prostate',
        option_2: 'Cancer du sein',
        option_3: 'Insuffisance hépatique sévère',
        option_4: 'Autre pathologie chronique'
      },
      note: `Certaines pathologies nécessitent une vigilance particulière avant de prescrire du dutastéride 
      • Cancer de la prostate : le dutastéride peut modifier les taux de PSA (antigène prostatique spécifique) et masquer l’évolution d’une pathologie prostatique. 
      • Cancer du sein chez l’homme : bien que rare, les agents inhibiteurs de la DHT peuvent influencer les cancers hormono-sensibles. 
      • Insuffisance hépatique sévère : le dutastéride est métabolisé par le foie ; une fonction hépatique réduite augmente le risque d’accumulation médicamenteuse. 
      • Autres pathologies chroniques : en raison de traitements concomitants susceptibles d’interagir. `,
      dependentQuestions: [
        {
          title: "veuillez préciser",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "medical_history_dependent",
          selectedOption: ['Autre pathologie chronique'],
          subQuestionId: 7
        }
      ],
      questionId: 7
    },
  ];

  const formQuestionsMaleEs = [
    {
      title: "Duración de la pérdida de Cabello",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "hair_loss_duration",
      optionMap:  {
        option_1: 'Menos de tres meses',
        option_2: 'Más de tres meses' 
      },
      note: `Es útil conocer desde cuándo el paciente presenta caída capilar. Una pérdida que dura menos de tres meses puede sugerir una condición transitoria, como el efluvio telógeno, frecuentemente asociado al estrés, enfermedades agudas, deficiencias nutricionales o incluso a efectos secundarios de tratamientos médicos (como los fármacos antimítoticos). Cuando la caída persiste más allá de tres meses, la causa más probable es la alopecia androgenética. `,
      questionId: 1
    },
    {
      title: "Tratamientos actuales y previos para la caída del cabello",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "hair_loss_treatment",
      optionMap:  {
        option_1: 'Sí, tratamiento previo',
        option_2: 'Sí, tratamiento actual',
        option_3: 'No'
      },
      note: `Esta pregunta es relevante, ya que muchos hombres con alopecia han probado distintos productos, incluidos medicamentos sujetos a prescripción médica.`,
      dependentQuestions: [
        {
          title: "especifique",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "hair_loss_treatment_dependent",
          selectedOption: ['Sí, tratamiento previo', 'Sí, tratamiento actual'],
          subQuestionId: 2
        }
      ],
      questionId: 2
    },
    {
      title: "Uso de inhibidores de la 5-alfa-reductasa",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "medications",
      optionMap:  {
        option_1: 'Sí',
        option_2: 'No' 
      },
      note: `Medicamentos como Avodart, Combodart, Duodart, Jalyn, dutasterida, Proscar y Propecia (finasterida) son fármacos orales que pueden haber sido prescritos para la caída del cabello o para afecciones prostáticas. Si el paciente está tomando actualmente alguno de estos medicamentos, no se debe combinar con dutasterida tópica adicional. Puede considerarse la sustitución del tratamiento oral por una formulación tópica, especialmente si el paciente refiere efectos adversos relacionados con la vía oral.`,
      dependentQuestions: [
        {
          title: "especifique",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "medications_dependent",
          selectedOption: ['Sí'],
          subQuestionId: 3
        }
      ],
      questionId: 3
    },
    {
      title: "Estado psicológico",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "psychological_state",
      optionMap:  {
        option_1: 'Sí',
        option_2: 'No' 
      },
      note: `Es importante saber si el paciente presenta actualmente síntomas depresivos o si está en tratamiento con antidepresivos. Las autoridades regulatorias (FDA, EMA) han señalado posibles efectos adversos sobre el estado de ánimo asociados a la finasterida, incluyendo ideación suicida. Este riesgo ha sido observado con las formulaciones orales. Aunque se ha indicado que la dutasterida no presenta el mismo perfil de riesgo, y que la formulación prescrita será tópica, se recomienda precaución.`,
      questionId: 4
    },
    {
      title: "Medicamentos actuales",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "allergies",
      optionMap:  {
        option_1: 'Sí',
        option_2: 'No' 
      },
      note: `Algunos medicamentos pueden interactuar con la dutasterida, aunque esto solo se ha demostrado mediante la medición de concentraciones plasmáticas en formulaciones orales. Se requiere especial precaución con los inhibidores potentes del citocromo P450 (ketoconazol, ritonavir, verapamilo, diltiazem), que pueden aumentar la concentración plasmática de dutasterida, así como con los inductores potentes (rifampicina, carbamazepina, dexametasona),  que pueden tener el efecto contrario. El metabolismo de las formulaciones tópicas podría no verse afectado, pero es útil conocer posibles interacciones.`,
      dependentQuestions: [
        {
          title: "especifique",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "allergies_dependent",
          selectedOption: ['Sí'],
          subQuestionId: 5
        }
      ],
      questionId: 5
    },
    {
      title: "Condiciones del cuero cabelludo",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "scalp_conditions",
      optionMap:  {
        option_1: 'Eccema',
        option_2: 'Placas rojas',
        option_3: 'Dermatitis seborreica',
        option_4: 'Psoriasis',
        option_5: 'Inflamación o descamació'
      },
      note: `Las afecciones inflamatorias o crónicas del cuero cabelludo pueden interferir con el uso seguro y eficaz de tratamientos tópicos. El eccema, la dermatitis, la psoriasis o la presencia de placas rojas pueden reducir la absorción, aumentar el riesgo de irritación o agravarse con la aplicación. En general, las patologías crónicas del cuero cabelludo deben considerarse una contraindicación para el uso tópico de dutasterida. `,
      questionId: 6
    },
    {
      title: "Antecedentes médicos",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "medical_history",
      optionMap:  {
        option_1: 'Cáncer de próstata',
        option_2: 'Cáncer de mama',
        option_3: 'Insuficiencia hepática grave',
        option_4: 'Otra enfermedad crónica'
      },
      note: `Algunas condiciones de salud requieren precaución especial antes de prescribir dutasterida: 
      • Cáncer de próstata: la dutasterida puede alterar los niveles de PSA (antígeno prostático específico) y enmascarar la progresión de enfermedades prostáticas. 
      • Cáncer de mama masculino: aunque poco frecuente, los agentes bloqueadores de la DHT pueden influir en tumores hormono-sensibles. 
      • Enfermedad hepática grave: la dutasterida se metaboliza en el hígado; una función hepática reducida aumenta el riesgo de acumulación del fármaco. 
      • Otras enfermedades crónicas: debido a posibles tratamientos concomitantes que podrían interactuar.`,
      dependentQuestions: [
        {
          title: "especifique",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "medical_history_dependent",
          selectedOption: ['Otra enfermedad crónica'],
          subQuestionId: 7
        }
      ],
      questionId: 7
    },
  ];

  const formQuestionsFemale = [
    {
      title: "Please confirm whether the patient has reached menopause, defined as the absence of menstrual periods for at least 12 consecutive months.",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "menopause_state",
      optionMap:  {
        option_1: 'Yes, the patient has reached menopause',
        option_2: 'No, the patient has not reached menopause' 
      },
      note: `Dutasteride, a key component of the hair loss treatment, is contraindicated in women who may become pregnant due to its teratogenic potential. Although topical formulations are believed to carry significantly lower systemic risk, caution remains essential. Confirmation of menopausal status is therefore required to ensure safe and responsible prescribing. Dutasteride should not be used in women of reproductive age. 
      In cases where the Sinclair scale indicates mild hair loss, natural anti-DHT alternatives may be considered. These include essential oils such as saw palmetto, tea tree, rosemary, and caffeine-based products, which are less potent but pose no known reproductive risk.`,
      questionId: 1
    },
    {
      title: "Onset of hair loss",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "start_of_hair_loss_during_menopause",
      optionMap:  {
        option_1: 'Yes, hair loss began during or after menopause',
        option_2: 'No, hair loss began before menopause' 
      },
      note: `Hair loss that begins prior to menopause may suggest alternative etiologies beyond androgenetic alopecia. These include thyroid dysfunction, systemic lupus erythematosus, nutritional deficiencies (particularly iron and zinc), or chemotherapy-related alopecia. Identifying and addressing the underlying cause may yield better outcomes than anti-DHT therapy alone, which may still be used as an adjunct`,
      questionId: 2
    },
    {
      title: "Menopausal hormone therapy",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "hormone_therapy",
      optionMap:  {
        option_1: 'Yes',
        option_2: 'No' 
      },
      note: `Is the patient currently receiving hormone replacement therapy for menopause? 
      This question is for informational purposes. While oestrogen therapy alters hormonal balance, it has minimal impact on postmenopausal hair loss.`,
      questionId: 3
    },
    {
      title: "History of hormone-sensitive breast cancer? ",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "hormone_sensitive_breast_cancer",
      optionMap:  {
        option_1: 'Yes',
        option_2: 'No' 
      },
      note: `Hormone-sensitive malignancies are influenced by oestrogens, progesterone, and androgens. Dutasteride, as a DHT-blocking agent, may affect oestrogen pathways when administered systemically. Postmenopausal women typically have low oestrogen levels and may be less susceptible to such effects. Topical dutasteride is considered to pose minimal systemic risk, but prescribing should remain at the clinician’s discretion.`,
      questionId: 4
    },
    {
      title: "History of endometriosis or polycystic ovary syndrome",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "history_of_endometriosis_or_uterine_fibroids",
      optionMap:  {
        option_1: 'Yes',
        option_2: 'No' 
      },
      note: `This question is for background context. Endometriosis is associated with elevated oestrogen levels and often improves post-menopause. Treatments such as progestins or aromatase inhibitors may contribute to hair loss. 
      PCOS is characterized by excess androgen production, including DHT, and commonly leads to early-onset androgenetic alopecia, hirsutism, and seborrhea. Patients with PCOS may have previously used spironolactone. Dutasteride is often particularly effective in these cases.`,
      questionId: 5
    },
    {
      title: "Mental health",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "depression_or_dosage_of_antidepressants",
      optionMap:  {
        option_1: 'Yes',
        option_2: 'No',
      },
      note: `Regulatory agencies including the FDA and EMA have issued warnings regarding finasteride and its potential association with suicidal ideation. Although dutasteride has not been linked to similar risks, caution is advised, particularly in patients with active depression. Topical formulations may carry lower risk, but use is not recommended in this context.`,
      questionId: 6
    },
    {
      title: "Medication allergies?",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "allergies",
      optionMap:  {
        option_1: 'Yes',
        option_2: 'No',
      },
      note: `This general screening helps identify individuals with atopic tendencies, who may be more prone to mild dermatologic reactions during initial application of topical products`,
      dependentQuestions: [
        {
          title: "Please specify",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "allergies_dependent",
          selectedOption: ['Yes'],
          subQuestionId: 7
        }
      ],
      questionId: 7
    },
    {
      title: "Known allergy to Dutasteride?",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "dutasteride_allergy",
      optionMap:  {
        option_1: 'Yes',
        option_2: 'No',
      },
      note: `Allergic reactions to 5-alpha reductase inhibitors are extremely rare. Two published cases involving finasteride have been documented: 
      Tresch S et al. T cell-mediated acute localized exanthematous pustulosis caused by finasteride. J Allergy Clin Immunol. 2012;129(2):589–94. 
      Moreno-Fernandez A et al. Urticarial rash due to finasteride. Allergy. 2010;65(3):405–6. Any known allergy to finasteride or dutasteride should be considered a contraindication to treatment.`,
      questionId: 8
    },
    {
      title: "Current medications use?",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "medications",
      optionMap:  {
        option_1: 'Yes',
        option_2: 'No',
      },
      note: `Please list all current medications. Particular attention should be given to the use of ketoconazole, verapamil, diltiazem, or ritonavir—whether by brand or generic name. These agents are potent CYP3A4 inhibitors and may significantly reduce the metabolic clearance of dutasteride, leading to elevated plasma concentrations and increased systemic risk. Dutasteride should not be prescribed to patients currently using these medications.`,
      dependentQuestions: [
        {
          title: "Please specify",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "medications_dependent",
          selectedOption: ['Yes'],
          subQuestionId: 9
        }
      ],
      questionId: 9
    },
    {
      title: "Use of finasteride or dutasteride-based products",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "other_medications",
      optionMap:  {
        option_1: 'Yes',
        option_2: 'No',
      },
      note: `These include oral formulations containing dutasteride or finasteride, marketed under names such as Avodart, Combodart, Duodart, Jalyn, Proscar, and Propecia. 
      Patients already using these agents may prefer a topical alternative, but concurrent use is not recommended. Oral treatment should be discontinued prior to initiating topical therapy with the same molecule.`,
      dependentQuestions: [
        {
          title: "Please specify",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "other_medications_dependent",
          selectedOption: ['Yes'],
          subQuestionId: 10
        }
      ],
      questionId: 10
    },
    {
      title: "Current hair loss treatment",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "hair_loss_treatment",
      optionMap:  {
        option_1: 'Yes',
        option_2: 'No',
      },
      note: `This question is for informational purposes only and does not influence eligibility for topical dutasteride.`,
      dependentQuestions: [
        {
          title: "Please specify",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "hair_loss_treatment_dependent",
          selectedOption: ['Yes'],
          subQuestionId: 11
        }
      ],
      questionId: 11
    },
    {
      title: "Scalp conditions",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "scalp_conditions",
      optionMap:  {
        option_1: 'Eczema',
        option_2: 'Red patches',
        option_3: 'Seborrheic dermatitis',
        option_4: 'Psoriasis',
        option_5: 'None'
      },
      note: `Topical treatments should not be prescribed in the presence of active scalp pathology. Such conditions may compromise absorption, increase the risk of local adverse reactions, and heighten the potential for hypersensitivity.`,
      questionId: 12
    },
    {
      title: "Recent use of hair or scalp products",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "recent_hair_products",
      optionMap:  {
        option_1: 'Yes',
        option_2: 'No',
      },
      note: `Has the patient recently applied any hair or scalp products such as thickening sprays, keratin treatments, styling agents, oils, or serums? 
      These products may interfere with the absorption of topical treatments. It is essential that the scalp be clean and free of residue prior to application. General counselling on scalp hygiene and product avoidance should accompany the prescription.`,
      questionId: 13
    },
  ];

  const formQuestionsFemaleFr = [
    {
      title: "Pouvez-vous confirmer que vous avez atteint la ménopause?",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "menopause_state",
      optionMap:  {
        option_1: 'Oui, j’ai atteint la ménopause',
        option_2: 'Non, je n’ai pas atteint la menopause' 
      },
      note: `Principe actif majeur dans le traitement de la chute de cheveux, le dutastéride est contre-indiqué chez les femmes susceptibles de concevoir en raison de son potentiel tératogène. Bien que les formulations topiques soient réputées présenter un risque systémique nettement inférieur, la prudence demeure impérative. Il est donc nécessaire de confirmer le statut ménopausique afin de garantir une prescription sûre et responsable. La dutastéride ne doit pas être utilisée chez les femmes en âge de procréer. Dans les cas où l’échelle de Sinclair indique une alopécie légère, il est possible d’envisager des alternatives naturelles à effet anti-DHT. Parmi celles-ci figurent certaines huiles essentielles comme celles du palmier nain (saw palmetto), de l’arbre à thé, de la menthe poivrée, ainsi que des actifs favorisant la microcirculation folliculaire comme le romarin ou la caféine. Ces substances naturelles sont moins puissantes, mais ne présentent à ce jour aucun risque connu sur le plan reproductif. `,
      questionId: 1
    },
    {
      title: "Perte de cheveux survenant avant la ménopause",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "start_of_hair_loss_during_menopause",
      optionMap:  {
        option_1: 'Oui, la perte de cheveux a commencé pendant ou après la ménopause',
        option_2: 'Non, la perte de cheveux a commencé avant la ménopause' 
      },
      note: `Une alopécie débutant avant la ménopause peut évoquer des étiologies alternatives à l’alopécie androgénétique. Parmi celles-ci figurent les dysfonctionnements thyroïdiens, le lupus érythémateux, les carences nutritionnelles (notamment en fer et en zinc), ou encore l’alopécie induite par chimiothérapie. L’identification et la prise en charge de la cause sous-jacente peuvent offrir de meilleurs résultats que le traitement anti-DHT seul, lequel peut néanmoins être envisagé en complément.`,
      questionId: 2
    },
    {
      title: "Traitement hormonal de la ménopause",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "hormone_therapy",
      optionMap:  {
        option_1: 'Oui',
        option_2: 'Non' 
      },
      note: `La patiente reçoit-elle actuellement un traitement hormonal substitutif dans le cadre de la ménopause? Cette question est à visée informative. Bien que le traitement œstrogénique modifie l’équilibre hormonal, son impact sur la perte de cheveux post-ménopausique reste limité.`,
      questionId: 3
    },
    {
      title: "Antécédents de cancer du sein hormono-sensible",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "hormone_sensitive_breast_cancer",
      optionMap:  {
        option_1: 'Oui',
        option_2: 'Non' 
      },
      note: `Les cancers hormono-dépendants sont influencés par les œstrogènes, la progestérone et les androgènes. Le dutastéride, en tant qu’inhibiteur de la DHT, peut interférer avec les voies œstrogéniques lorsqu’il est administré par voie systémique. Chez les femmes ménopausées, les taux d’œstrogènes étant généralement faibles, la susceptibilité à ces effets est réduite. Le dutastéride topique est considéré comme présentant un risque systémique minime, mais sa prescription doit rester à l’appréciation du clinicien.`,
      questionId: 4
    },
    {
      title: "Antécédents d’endométriose ou de syndrome des ovaires polykystiques (SOPK)",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "history_of_endometriosis_or_uterine_fibroids",
      optionMap:  {
        option_1: 'Oui',
        option_2: 'Non' 
      },
      note: `Cette question vise à fournir un contexte clinique. L’endométriose est associée à des taux élevés d’œstrogènes et tend donc à s’améliorer après la ménopause. Les traitements utilisés dans cette indication, tels que les progestatifs ou les inhibiteurs de l’aromatase, peuvent avoir contribué à la chute de cheveux. Le SOPK se caractérise par une production excessive d’androgènes, notamment de DHT, et entraîne fréquemment une alopécie androgénétique précoce, un hirsutisme et une séborrhée. Les patientes atteintes de SOPK ont souvent été traitées par de la spironolactone. Le dutastéride est particulièrement efficace dans ces cas.`,
      questionId: 5
    },
    {
      title: "Santé mentale",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "depression_or_dosage_of_antidepressants",
      optionMap:  {
        option_1: 'Oui',
        option_2: 'Non',
      },
      note: `Les agences réglementaires du médicament, telles que la FDA et l’EMA, ont émis des mises en garde concernant le finastéride et son association potentielle avec des idées suicidaires. Bien que le dutastéride n’ait pas été lié à des risques similaires, la prudence est recommandée, en particulier chez les patientes souffrant de dépression active. Les formulations topiques pourraient présenter un risque moindre, mais leur utilisation doit être évaluée en function du contexte.`,
      questionId: 6
    },
    {
      title: "Allergies médicamenteuses",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "allergies",
      optionMap:  {
        option_1: 'Oui',
        option_2: 'Non',
      },
      note: `Ce dépistage général permet d’identifier les personnes présentant une tendance atopique, susceptibles de développer des réactions dermatologiques légères lors des premières applications de produits à usage local.`,
      dependentQuestions: [
        {
          title: "veuillez préciser",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "allergies_dependent",
          selectedOption: ['Oui'],
          subQuestionId: 7
        }
      ],
      questionId: 7
    },
    {
      title: "Allergie connue au dutastéride",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "dutasteride_allergy",
      optionMap:  {
        option_1: 'Oui',
        option_2: 'Non',
      },
      note: `Les réactions allergiques aux inhibiteurs de la 5-alpha réductase sont extrêmement rares. Deux cas publiés impliquant la finastéride ont été documentés : 
      • Tresch S et al. T cell-mediated acute localized exanthematous pustulosis caused by finasteride. J Allergy Clin Immunol. 2012;129(2):589–94. 
      • Moreno-Fernandez A et al. Urticarial rash due to finasteride. Allergy. 2010;65(3):405–6.
      Toute allergie connue au finastéride ou au dutastéride doit être considérée comme une contre-indication au traitement.`,
      questionId: 8
    },
    {
      title: "Médicaments actuellement utilisés",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "medications",
      optionMap:  {
        option_1: 'Oui',
        option_2: 'Non',
      },
      note: `Veuillez indiquer tous les traitements médicamenteux en cours. Une attention particulière doit être portée à l’utilisation de kétoconazole, vérapamil, diltiazem ou ritonavir — qu’ils soient prescrits sous leur nom générique ou commercial. Ces substances sont de puissants inhibiteurs du CYP3A4 et peuvent réduire significativement la clairance métabolique du dutastéride, entraînant une élévation de sa concentration plasmatique et un risque systémique accru. Le dutastéride ne doit pas être prescrit chez les patients utilisant actuellement ces médicaments.`,
      dependentQuestions: [
        {
          title: "veuillez préciser",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "medications_dependent",
          selectedOption: ['Oui'],
          subQuestionId: 9
        }
      ],
      questionId: 9
    },
    {
      title: "Utilisation de produits à base de finastéride ou de dutastéride",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "other_medications",
      optionMap:  {
        option_1: 'Oui',
        option_2: 'Non',
      },
      note: `Cela inclut les formulations orales contenant du dutastéride ou du finastéride, commercialisées sous des noms tels que Avodart, Combodart, Duodart, Jalyn, Proscar et Propecia. Les patients déjà sous traitement par ces agents peuvent préférer une alternative topique, mais l’usage concomitant de la formulation orale n’est pas recommandé. Le traitement oral doit être interrompu avant d’initier une thérapie topique avec la même molécule. `,
      dependentQuestions: [
        {
          title: "Si oui, veuillez préciser",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "other_medications_dependent",
          selectedOption: ['Oui'],
          subQuestionId: 10
        }
      ],
      questionId: 10
    },
    {
      title: "Traitement actuel contre la chute de cheveux",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "hair_loss_treatment",
      optionMap:  {
        option_1: 'Oui',
        option_2: 'Non',
      },
      note: `Cette question est uniquement à visée informative et n’influence pas l’éligibilité au traitement topique par dutastéride. Le clinicien tiendra compte des traitements topiques en cours et pourra envisager leur arrêt avant d’instaurer une formulation à base de dutastéride.`,
      dependentQuestions: [
        {
          title: "veuillez préciser",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "hair_loss_treatment_dependent",
          selectedOption: ['Oui'],
          subQuestionId: 11
        }
      ],
      questionId: 11
    },
    {
      title: "Pathologies du cuir chevelu",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "scalp_conditions",
      optionMap:  {
        option_1: 'Eczéma',
        option_2: 'Plaques rouges',
        option_3: 'Dermatite séborrhéique',
        option_4: 'Psoriasis',
        option_5: 'Aucune'
      },
      note: `Les traitements topiques ne doivent pas être prescrits en présence de pathologies actives du cuir chevelu. Ces affections peuvent altérer l’absorption du produit, augmenter le risque de réactions locales indésirables et accroître la probabilité d’hypersensibilité.`,
      questionId: 12
    },
    {
      title: "Utilisation récente de produits capillaires ou pour le cuir chevelu",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "recent_hair_products",
      optionMap:  {
        option_1: 'Oui',
        option_2: 'Non',
      },
      note: `La patiente a-t-elle récemment appliqué des produits capillaires tels que des sprays volumateurs, des traitements à la kératine, des produits coiffants, des huiles ou des sérums ? Ces produits peuvent interférer avec l’absorption du traitement topique. Il est essentiel que le cuir chevelu soit propre et exempt de résidus avant toute application. Il importe de conseiller la patiente sur l’hygiène du cuir chevelu et recommander l’éviction de certains produits.`,
      questionId: 13
    },
  ];

  const formQuestionsFemaleEs = [
    {
      title: "Confirmación del estado menopáusico",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "menopause_state",
      optionMap:  {
        option_1: 'Sí, la paciente ha alcanzado la menopausia',
        option_2: 'No, la paciente no ha alcanzado la menopausia' 
      },
      note: `Confirme si la paciente ha alcanzado la menopausia, definida como la ausencia de menstruaciones durante al menos 12 meses consecutivos. La dutasterida, componente clave en el tratamiento de la alopecia, está contraindicada en mujeres que puedan quedar embarazadas debido a su potencial teratogénico. Aunque se considera que las formulaciones tópicas conllevan un riesgo sistémico significativamente menor, la precaución sigue siendo esencial. Por ello, es necesario confirmar el estado menopáusico para garantizar una prescripción segura y responsable. La dutasterida no debe utilizarse en mujeres en edad fértil. En los casos en que la escala de Sinclair indique una pérdida de cabello leve, pueden considerarse alternativas naturales con efecto anti-DHT. Estas incluyen aceites esenciales como el extracto de sabal (saw palmetto), árbol de té, menta, o productos que mejoran la circulación sanguínea en los folículos, como el romero o la cafeína. Estos ingredientes naturales son menos potentes, pero no presentan riesgos reproductivos conocidos.`,
      questionId: 1
    },
    {
      title: "Inicio de la pérdida de cabello",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "start_of_hair_loss_during_menopause",
      optionMap:  {
        option_1: 'Sí, la pérdida de cabello comenzó durante o después de la menopausia',
        option_2: 'No, la pérdida de cabello comenzó antes de la menopausia' 
      },
      note: `La pérdida de cabello que comienza antes de la menopausia puede sugerir etiologías alternativas a la alopecia androgenética. Entre ellas se incluyen disfunción tiroidea, lupus eritematoso sistémico, deficiencias nutricionales (especialmente de hierro y zinc), o alopecia secundaria a quimioterapia. Identificar y tratar la causa subyacente puede ofrecer mejores resultados que la monoterapia anti-DHT, la cual puede utilizarse como complemento.`,
      questionId: 2
    },
    {
      title: "Tratamiento hormonal menopáusico",
      type: "radio",
      placeholder: "Enter your answer",
      commonIdentifier: "hormone_therapy",
      optionMap:  {
        option_1: 'Sí',
        option_2: 'No' 
      },
      note: `¿La paciente está recibiendo actualmente terapia hormonal sustitutiva para la menopausia? Esta pregunta tiene fines informativos. Aunque la terapia con estrógenos modifica el equilibrio hormonal, su impacto sobre la pérdida de cabello postmenopáusica es mínimo.`,
      questionId: 3
    },
    {
      title: "Antecedentes de cáncer de mama hormonosensible",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "hormone_sensitive_breast_cancer",
      optionMap:  {
        option_1: 'Sí',
        option_2: 'No' 
      },
      note: `Las neoplasias hormonosensibles están influenciadas por los estrógenos, la progesterona y los andrógenos. La dutasterida, como inhibidor de la DHT, puede afectar las vías estrogénicas cuando se administra por vía sistémica. Las mujeres postmenopáusicas suelen tener niveles bajos de estrógenos y pueden ser menos susceptibles a estos efectos. Se considera que la dutasterida tópica conlleva un riesgo sistémico mínimo, pero su prescripción debe quedar a criterio del médico tratante.`,
      questionId: 4
    },
    {
      title: "Antecedentes de endometriosis o síndrome de ovario poliquístico (SOP)",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "history_of_endometriosis_or_uterine_fibroids",
      optionMap:  {
        option_1: 'Sí',
        option_2: 'No' 
      },
      note: `Esta pregunta tiene fines contextuales. La endometriosis se asocia con niveles elevados de estrógenos y suele mejorar tras la menopausia. Los tratamientos utilizados para la endometriosis, como los progestágenos o los inhibidores de la aromatasa, pueden contribuir a la caída del cabello. El SOP se caracteriza por una producción excesiva de andrógenos, incluida la DHT, y suele provocar alopecia androgenética de inicio temprano, hirsutismo y seborrea. Las pacientes con SOP pueden haber utilizado previamente espironolactona. En estos casos, la dutasterida suele ser particularmente eficaz.`,
      questionId: 5
    },
    {
      title: "Salud mental",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "depression_or_dosage_of_antidepressants",
      optionMap:  {
        option_1: 'Sí',
        option_2: 'No',
      },
      note: `Organismos reguladores como la FDA y la EMA han emitido advertencias sobre la finasterida y su posible asociación con ideación suicida. Aunque no se ha vinculado la dutasterida con riesgos similares, se recomienda precaución, especialmente en pacientes con depresión activa. Las formulaciones tópicas podrían presentar un riesgo menor, pero su uso debe evaluarse en función del contexto.`,
      questionId: 6
    },
    {
      title: "Alergias a medicamentos",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "allergies",
      optionMap:  {
        option_1: 'Sí',
        option_2: 'No',
      },
      note: `Este cribado general permite identificar pacientes con tendencia atópica, quienes podrían ser más propensos a desarrollar reacciones dermatológicas leves durante las primeras aplicaciones de productos tópicos.`,
      dependentQuestions: [
        {
          title: "especifique",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "allergies_dependent",
          selectedOption: ['Sí'],
          subQuestionId: 7
        }
      ],
      questionId: 7
    },
    {
      title: "Alergia conocida a la dutasterida",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "dutasteride_allergy",
      optionMap:  {
        option_1: 'Sí',
        option_2: 'No',
      },
      note: `Las reacciones alérgicas a los inhibidores de la 5-alfa reductasa son extremadamente raras. Se han documentado dos casos publicados que implican a la finasterida: • Tresch S et al. T cell-mediated acute localized exanthematous pustulosis caused by finasteride. J Allergy Clin Immunol. 2012;129(2):589–94. • Moreno-Fernandez A et al. Urticarial rash due to finasteride. Allergy. 2010;65(3):405–6. Toda alergia conocida a la finasterida o a la dutasterida debe considerarse una contraindicación para el tratamiento. `,
      questionId: 8
    },
    {
      title: "Medicación actual utilizada",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "medications",
      optionMap:  {
        option_1: 'Sí',
        option_2: 'No',
      },
      note: `Por favor, indique todos los medicamentos que esté utilizando actualmente. Se debe prestar especial atención al uso de ketoconazol, verapamilo, diltiazem o ritonavir—ya sea por nombre comercial o genérico. Estos agentes son inhibidores potentes del CYP3A4 y pueden reducir significativamente la depuración metabólica de la dutasterida, lo que conduce a concentraciones plasmáticas elevadas y un mayor riesgo sistémico. La dutasterida no debe prescribirse a pacientes que estén utilizando actualmente estos medicamentos.`,
      dependentQuestions: [
        {
          title: "especifique",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "medications_dependent",
          selectedOption: ['Sí'],
          subQuestionId: 9
        }
      ],
      questionId: 9
    },
    {
      title: "Uso de productos a base de finasterida o dutasterida",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "other_medications",
      optionMap:  {
        option_1: 'Sí',
        option_2: 'No',
      },
      note: `Esto incluye formulaciones orales que contienen dutasterida o finasterida, comercializadas bajo nombres como Avodart, Combodart, Duodart, Jalyn, Proscar y Propecia. Los pacientes que ya utilizan estos agentes pueden preferir una alternativa tópica, pero no se recomienda el uso concomitante. El tratamiento oral debe suspenderse antes de iniciar la terapia tópica con la misma molécula. `,
      dependentQuestions: [
        {
          title: "Si la respuesta es sí, especifique",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "other_medications_dependent",
          selectedOption: ['Sí'],
          subQuestionId: 10
        }
      ],
      questionId: 10
    },
    {
      title: "Tratamiento actual para la pérdida de cabello",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "hair_loss_treatment",
      optionMap:  {
        option_1: 'Sí',
        option_2: 'No',
      },
      note: `Esta pregunta tiene fines informativos únicamente y no influye en la elegibilidad para la dutasterida tópica. El clínico considerará especialmente los tratamientos tópicos actuales y podrá considerar suspenderlos antes de prescribir una formulación con dutasterida.`,
      dependentQuestions: [
        {
          title: "especifique",
          type: "text",
          placeholder: "Enter your answer",
          commonIdentifier: "hair_loss_treatment_dependent",
          selectedOption: ['Sí'],
          subQuestionId: 11
        }
      ],
      questionId: 11
    },
    {
      title: "Condiciones del cuero cabelludo",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "scalp_conditions",
      optionMap:  {
        option_1: 'Eccema',
        option_2: 'Placas rojas',
        option_3: 'Dermatitis seborreica',
        option_4: 'Psoriasis',
        option_5: 'Ninguna'
      },
      note: `No se deben prescribir tratamientos tópicos en presencia de patologías activas del cuero cabelludo. Estas condiciones pueden comprometer la absorción, aumentar el riesgo de reacciones adversas locales y elevar el potencial de hipersensibilidad.`,
      questionId: 12
    },
    {
      title: "Uso reciente de productos capilares o para el cuero cabelludo",
      type: "radio",
      placeholder: "Select your answer",
      commonIdentifier: "recent_hair_products",
      optionMap:  {
        option_1: 'Sí',
        option_2: 'No',
      },
      note: `¿El paciente ha aplicado recientemente algún producto capilar o para el cuero cabelludo como sprays voluminizadores, tratamientos de keratina, agentes de peinado, aceites o sueros? Estos productos pueden interferir con la absorción de los tratamientos tópicos. Es esencial que el cuero cabelludo esté limpio y libre de residuos antes de la aplicación. El asesoramiento general sobre higiene del cuero cabelludo y la evitación de productos debe acompañar la prescripción. `,
      questionId: 13
    },
  ];

  useEffect(() => {
    if(location?.state?.user_obj?.gender && location.state.user_obj.gender){
      setGender(location.state.user_obj.gender)
      if (location?.state?.user_obj?.medical_history_present && location.state.user_obj.medical_history_present) {
        getHealthAnalysis()
      }
      if(location.state.user_obj.gender.toUpperCase() === 'MALE') {
        locale && locale === 'fr' ? setFormQuestions(formQuestionsMaleFr) : locale && locale == 'es' ? setFormQuestions(formQuestionsMaleEs) : setFormQuestions(formQuestionsMale)
        setFormValues(formValuesMale)
      } else if(location.state.user_obj.gender.toUpperCase() === 'FEMALE') {
        locale && locale === 'fr' ? setFormQuestions(formQuestionsFemaleFr) : locale && locale == 'es' ? setFormQuestions(formQuestionsFemaleEs) : setFormQuestions(formQuestionsFemale)
        setFormValues(formValuesFemale)
      }
    }
  }, [])

  useEffect(() => {
    console.log("previewFormQuestions", previewFormQuestions)
  }, [previewFormQuestions])

  const getHealthAnalysis = async () => {
    setIsLoading(true)
    let params = {
      user_id: location.state.user_obj.user_id
      // user_id: 1
    }
    console.log('get_medical_history_for_user params', params);
    try {
      let res = await postApi('get_medical_history_for_user', params)
      if(res){
        setIsLoading(false)
        console.log('get_medical_history_for_user res', res); 
        const yesByLocale = {
          fr: "Oui",
          es: "Sí",
          en: "Yes"
        };
        const key = yesByLocale[locale] || "Yes";
        res.medications = {[key] : 'dependent'}
        Object.keys(res).length > 0 && Object.keys(res).map((k) => {
          addValues(k, res[k])
        })
      }
    } catch (err) {
      console.log("err", err);
      setIsLoading(false)
      serviceDown()
    }
  }

  const addValues = (key, val) => {
    let formQues = 
      location.state.user_obj.gender.toUpperCase() === 'MALE' && locale && locale == 'en' ? 
        formQuestionsMale 
          :
      location.state.user_obj.gender.toUpperCase() === 'MALE' && locale && locale == 'fr' ? 
          formQuestionsMaleFr 
            :
      location.state.user_obj.gender.toUpperCase() === 'MALE' && locale && locale == 'es' ? 
          formQuestionsMaleEs 
            :
      location.state.user_obj.gender.toUpperCase() === 'FEMALE' && locale && locale === 'fr' ? 
          formQuestionsFemaleFr 
            : 
      location.state.user_obj.gender.toUpperCase() === 'FEMALE' && locale && locale == 'es' ? 
          formQuestionsFemaleEs 
            : 
          formQuestionsFemale
    formQues.length > 0 && formQues.forEach((qObj) => {
      if (qObj.commonIdentifier === key) {
        if(typeof val === 'object' && qObj?.dependentQuestions?.length > 0) {
          qObj.dependentQuestions[0]['value'] = Object.values(val)[0]
          qObj['value'] = Object.keys(val)[0]
        } else {
          qObj['value'] = val;
        }
        setPreviewFormQuestions(prev => [...prev, qObj]); 
      }
    });
  };

  const handleFormValues = (value, identifier, parent) => {
    let finalVal = {}
    if (
      identifier === 'hair_loss_treatment' || 
      identifier === 'medications' ||
      identifier === 'allergies' ||
      identifier === 'medical_history' ||
      identifier === 'allergies' ||
      identifier === 'other_medications'
    ) {
      if(value.toUpperCase() === 'NO'){
        finalVal[`${value}`] = value
      } else {
        finalVal[`${value}`] = value
      }
    } else if (
      identifier === 'medications_dependent' ||
      identifier === 'allergies_dependent' ||
      identifier === 'medical_history_dependent' ||
      identifier === 'allergies_dependent' ||
      identifier === 'other_medications_dependent'
    ) {
      const yesByLocale = {
        fr: "Oui",
        es: "Sí",
        en: "Yes"
      };
      const key = yesByLocale[locale] || "Yes";
      finalVal[key] = value
    } else if (
      identifier === 'hair_loss_treatment_dependent'
    ) {
      finalVal[parent.selectedParent] = value
    } else if (
      identifier === 'scalp_conditions'
    ) {
      finalVal[`${value}`] = value
    } else {
      finalVal = value
    } 

    if(gender.toUpperCase() === 'MALE') {
      setFormValuesMale((prev) => {
        const updated = { ...prev };
  
        switch (identifier) {
          case "hair_loss_treatment":
            updated.hair_loss_treatment = finalVal;
            break;
          case "hair_loss_treatment_dependent":
            updated.hair_loss_treatment = finalVal;
            break;
          case "medications":
            updated.medications = finalVal;
            break;
          case "medications_dependent":
            updated.medications = finalVal;
            break;
          case "allergies":
            updated.allergies = finalVal;
            break;
          case "allergies_dependent":
            updated.allergies = finalVal;
            break;
          case "hair_loss_duration":
            updated.hair_loss_duration = finalVal;
            break;
          case "psychological_state":
            updated.psychological_state = finalVal;
            break;
          case "medical_history":
            updated.medical_history = finalVal;
            break;
          case "medical_history_dependent":
            updated.medical_history = finalVal;
            break;
          case "scalp_conditions":
            updated.scalp_conditions = finalVal;
            break;
          default:
            break;
        }
        return updated;
      });
    } else {
      setFormValuesFemale((prev) => {
        const updated = { ...prev };

        switch (identifier) {
          case "menopause_state":
            updated.menopause_state = finalVal;
            break;
          case "start_of_hair_loss_during_menopause":
            updated.start_of_hair_loss_during_menopause = finalVal;
            break;
          case "hormone_therapy":
            updated.hormone_therapy = finalVal;
            break;
          case "hormone_sensitive_breast_cancer":
            updated.hormone_sensitive_breast_cancer = finalVal;
            break;
          case "history_of_endometriosis_or_uterine_fibroids":
            updated.history_of_endometriosis_or_uterine_fibroids = finalVal;
            break;
          case "depression_or_dosage_of_antidepressants":
            updated.depression_or_dosage_of_antidepressants = finalVal;
            break;
          case "allergies":
            updated.allergies = finalVal;
            break;
          case "allergies_dependent":
            updated.allergies = finalVal;
            break;
          case "dutasteride_allergy":
            updated.dutasteride_allergy = finalVal;
            break;
          case "medications":
            updated.medications = finalVal;
            break;
          case "medications_dependent":
            updated.medications = finalVal;
            break;
          case "other_medications":
            updated.other_medications = finalVal;
            break;
          case "other_medications_dependent":
            updated.other_medications = finalVal;
            break;
          case "hair_loss_treatment":
            updated.hair_loss_treatment = finalVal;
            break;
          case "hair_loss_treatment_dependent":
            updated.hair_loss_treatment = finalVal;
            break;
          case "scalp_conditions":
            updated.scalp_conditions = finalVal;
            break;
          case "recent_hair_products":
            updated.recent_hair_products = finalVal;
            break;
          default:
            break;
        }
        return updated;
      });
    }
  }
  
  const handleDeclaration = (value, identifier) => {
    setDeclarations((prev) => {
      const updated = { ...prev };

      switch (identifier) {
        case "dec1":
          updated.dec1 = value;
          break;
        case "dec2":
          updated.dec2 = value;
          break;
        case "dec3":
          updated.dec3 = value;
          break;
        case "dec4":
          updated.dec4 = value;
          break;
        default:
          break;
      }
      return updated;
    });
  }

  const handleSubmit = async () => {
    setSubmitClicked(true);
    if(
      (gender.toUpperCase () === 'MALE' && (
        formValuesMale.hair_loss_treatment === '' || 
        formValuesMale.medications === '' ||
        formValuesMale.allergies === '' || 
        formValuesMale.hair_loss_duration === '' ||
        formValuesMale.psychological_state === '' || 
        formValuesMale.medical_history === '' ||
        formValuesMale.scalp_conditions === ''
      )) || 
      (gender.toUpperCase () === 'FEMALE' && (
        formValuesFemale.menopause_state === '' ||
        formValuesFemale.start_of_hair_loss_during_menopause === '' ||
        formValuesFemale.hormone_therapy === '' ||
        formValuesFemale.hormone_sensitive_breast_cancer === '' ||
        formValuesFemale.history_of_endometriosis_or_uterine_fibroids === '' ||
        formValuesFemale.depression_or_dosage_of_antidepressants === '' ||
        formValuesFemale.allergies === '' ||
        formValuesFemale.dutasteride_allergy === '' ||
        formValuesFemale.medications === '' ||
        formValuesFemale.other_medications === '' ||
        formValuesFemale.hair_loss_treatment === '' ||
        formValuesFemale.scalp_conditions === '' ||
        formValuesFemale.recent_hair_products === ''
      )) 
    ) {
      swal({
        title: i18next.t("Please fill all the fields"),
        icon: "warning",
      })
    } else if (!declarations.dec1 || !declarations.dec2 || !declarations.dec4) {
      return
    } else {
      if(gender.toUpperCase() === "MALE") {
        formValuesMale.user_id = location.state.user_obj.user_id
        formValuesMale.date_of_birth = location.state.user_obj.year_of_birth 
        addDeclarationMale()
      } else {
        formValuesFemale.user_id = location.state.user_obj.user_id
        formValuesFemale.date_of_birth = location.state.user_obj.year_of_birth
        addDeclarationFemale()
      }
    }
  }

  const addDeclarationMale = async () => {
    setIsLoading(true)
    let params = {
      user_id: formValuesMale.user_id,
      date_of_birth: formValuesMale.date_of_birth,
      hair_loss_treatment: formValuesMale.hair_loss_treatment,
      medications: formValuesMale.medications,
      allergies: formValuesMale.allergies,
      hair_loss_duration: formValuesMale.hair_loss_duration,
      psychological_state: formValuesMale.psychological_state,
      medical_history: formValuesMale.medical_history,
      scalp_conditions: formValuesMale.scalp_conditions
    }
    console.log('add_medical_history params', params);
    try {
      let res = await postApi('add_medical_history', params)
      console.log('add_medical_history res', res);
      if(res) {
        setIsLoading(false)
        // navigate('/clients', {state: {user_obj: location.state.user_obj}})
        // navigate('/prescriptions', {state: {user_obj: location.state.user_obj}})
        swal({
          title: i18next.t("Health declaration submitted successfully"),
          icon: "success",
        }).then((val) => {
          if(val) {
            navigate('/clients')
          }
        });
      }
    } catch (err) {
      console.log("err", err);
      setIsLoading(false)
      serviceDown()
    }
  }

  const addDeclarationFemale = async () => {
    setIsLoading(true)
    let params = {
      user_id: formValuesFemale.user_id,
      date_of_birth: formValuesFemale.date_of_birth,
      menopause_state: formValuesFemale.menopause_state,
      start_of_hair_loss_during_menopause: formValuesFemale.start_of_hair_loss_during_menopause,
      hormone_therapy: formValuesFemale.hormone_therapy,
      hormone_sensitive_breast_cancer: formValuesFemale.hormone_sensitive_breast_cancer,
      history_of_endometriosis_or_uterine_fibroids: formValuesFemale.history_of_endometriosis_or_uterine_fibroids,
      depression_or_dosage_of_antidepressants: formValuesFemale.depression_or_dosage_of_antidepressants,
      allergies: formValuesFemale.allergies,
      dutasteride_allergy: formValuesFemale.dutasteride_allergy,
      medications: formValuesFemale.medications,
      other_medications: formValuesFemale.other_medications,
      hair_loss_treatment: formValuesFemale.hair_loss_treatment,
      scalp_conditions: formValuesFemale.scalp_conditions,
      recent_hair_products: formValuesFemale.recent_hair_products
    }
    console.log('add_medical_history_female params', params);
    try {
      let res = await postApi('add_medical_history_female', params)
      console.log('add_medical_history_female res', res);
      if(res) {
        setIsLoading(false)
        // navigate('/prescriptions', {state: {user_obj: location.state.user_obj}})
        swal({
          title: i18next.t("Health declaration submitted successfully"),
          icon: "success",
        }).then((val) => {
          if(val) {
            navigate('/clients')
          }
        });
      }
    } catch (err) {
      console.log("err", err);
      setIsLoading(false)
      serviceDown()
    }
  }

  return (
    <>
      {isLoading && <MainLoader/>}
      <div className='healthdeclarationpage photo-container main-section-container'>
        <Grid container spacing={0} className='d-flex justify-content-start align-items-center'>
          <Grid item xs={1} className=''>
            <div className='d-flex justify-content-start align-items-center'>
              <span className='backButtonDiv' onClick={() => navigate(-1, { state: { goTo: location?.state?.from }})}>
                <ArrowBackOutlinedIcon className='arrowBackBtn'></ArrowBackOutlinedIcon>
              </span>
            </div>
          </Grid>
          <Grid item xs={10} className='d-flex justify-content-center align-items-center '>
            <Typography variant="h6" className="title">{i18next.t("Health Declaration")}</Typography>
          </Grid>
          <Grid item xs={1} className='d-flex justify-content-center align-items-center'>
            <Sidebar/>
          </Grid>
        </Grid>
        <div className='healthdeclarationpage-questions-container'>
          <div className='d-flex justify-content-start align-items-start patientCard mt-10px flex-direction-column'>
            <p><span className='p-main'>{i18next.t('Full Name: ')}</span>{`${location?.state?.user_obj?.first_name} ${location?.state?.user_obj?.last_name}`}</p>
            <p><span className='p-main'>{i18next.t('Year of Birth: ')}</span>{`${location?.state?.user_obj?.year_of_birth}`}</p>
            <p><span className='p-main'>{i18next.t('Gender: ')}</span>{`${location?.state?.user_obj?.gender}`}</p>
          </div>
          <div className={!location?.state?.user_obj.medical_history_present ? 'healthdeclarationquestioncontainer' : 'healthdeclarationquestioncontainer preview'}>
            <div className='formMainComponent'>
              <>
                {
                  location?.state?.user_obj.medical_history_present ? 
                    previewFormQuestions?.length > 0 && previewFormQuestions.map((ele) => {
                      return (
                        <Grid item xs={12} className='d-flex justify-content-start align-items-center'> 
                          <FormQuestionsPreview 
                            element={ele} 
                            handleFormValues={handleFormValues}
                            formQuestions={formQuestions}
                            formValues={formValues}
                            showQuesAns={true}
                          />   
                        </Grid>
                      )
                    })   
                      :
                    formQuestions?.length > 0 && formQuestions.map((ele) => {
                      return (
                        <Grid item xs={12} className='d-flex justify-content-start align-items-start'> 
                          <FormQuestions 
                            element={ele} 
                            handleFormValues={handleFormValues}
                            formQuestions={formQuestions}
                            formValues={formValues}
                          />   
                        </Grid>
                      )
                    })   
                }
              </>
            </div>
            {
              !location?.state?.user_obj.medical_history_present &&
                <>
                  <Grid item xs={12} className={!declarations.dec1 && submitClicked ? 'd-flex justify-content-start align-items-center unchecked' : 'd-flex justify-content-start align-items-center'}>
                    <FormGroup>
                      <FormControlLabel control={<Checkbox onChange={(e) => handleDeclaration(e.target.checked, 'dec1')}/>} label='I can request a copy of my prescription at any time to inform my doctor about the medication prescribed for alopecia.'/>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} className={!declarations.dec2 && submitClicked ? 'd-flex justify-content-start align-items-center  mt-10px unchecked' : 'd-flex justify-content-start align-items-center  mt-10px'}>
                    <FormGroup>
                      <FormControlLabel control={<Checkbox onChange={(e) => handleDeclaration(e.target.checked, 'dec2')}/>} label='I confirm that this treatment is for my personal and exclusive use. I commit to being the sole user.'/>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} className='d-flex justify-content-start align-items-center  mt-10px'>
                    <FormGroup>
                      <FormControlLabel control={<Checkbox onChange={(e) => handleDeclaration(e.target.checked, 'dec3')}/>} label='I commit to submitting a photo of my scalp once a month to assess the effectiveness of the treatment and possible adjustments.'/>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} className={!declarations.dec4 && submitClicked ? 'd-flex justify-content-start align-items-center  mt-10px mb-20px unchecked' : 'd-flex justify-content-start align-items-center  mt-10px mb-20px'}>
                    <FormGroup>
                      <FormControlLabel control={<Checkbox onChange={(e) => handleDeclaration(e.target.checked, 'dec4')}/>} label='I have read and understood the above questions and have answered them honestly and truthfully.'/>
                    </FormGroup>
                  </Grid>
                </>
            }
          </div>
          {
            !location?.state?.user_obj.medical_history_present &&
              <Button className='submitButton commonButton mt-10px' variant="contained" onClick={handleSubmit}>
                {i18next.t("Submit")}
              </Button>
          }
        </div>
      </div>
    </>
  )
}

export default HealthDeclaration